import AOS from 'aos';

/**
 * One place for the AOS settings.
 *
 * Five components used to call a bare `AOS.init()`, which does not merely
 * initialise them - it resets the WHOLE page to the library defaults: 400ms
 * instead of the 800ms custom.js asks for, `ease` instead of the configured
 * easing, and `once: false`, which makes every element replay its entrance each
 * time it scrolls back into view. Whichever component mounted last decided how
 * the rest of the site animated. They all pass this now.
 *
 * `ease-out-cubic` replaces `easing: 'slide'`, which is not an AOS easing at
 * all - AOS fell back to linear, which is what made the entrances read as
 * abrupt. Cubic ease-out decelerates into the resting position.
 */
export const AOS_OPTIONS = {
   duration: 800,
   easing: 'ease-out-cubic',
   once: true,
   offset: 80,
};

/** Raised on `document` by a view once its loading overlay has gone. */
export const PAGE_REVEALED = 'mea:page-revealed';

/**
 * Whether the page is currently on screen rather than behind a loader.
 *
 * Views do not all render their content the same way. TemplateRenderer resolves
 * `<component :is>` from store state, so its hero can mount AFTER the reveal has
 * already been announced - and a hero that starts waiting for an event which has
 * already fired holds itself invisible until the safety timeout. That is the
 * empty Loyalty Management hero. Tracking the state, rather than only the event,
 * lets a late arrival see that it has nothing to wait for.
 */
let pageRevealed = false;

/** Called by a view when it puts its loading overlay up. */
export function beginPageLoad() {
   pageRevealed = false;
}

export function initAOS() {
   AOS.init(AOS_OPTIONS);
}

/**
 * Re-measures every element's trigger point. Worth doing after content has
 * landed and moved things down the page, since AOS caches those positions at
 * init and would otherwise fire against stale ones.
 */
export function refreshAOS() {
   AOS.refreshHard();
}

/** Called by a view the moment it stops covering the page with a loader. */
export function announcePageRevealed() {
   pageRevealed = true;
   document.dispatchEvent(new CustomEvent(PAGE_REVEALED));
}

/**
 * Plays a hero entrance exactly once, when the page can actually be seen.
 *
 * AOS begins its scan on load, so on any view that sits behind a loading
 * overlay the entrance runs and finishes unseen and the heading simply appears.
 * Every hero worked around that the same way: strip `aos-animate` on a timer
 * and put it back 700ms later. But removing that class while the element is on
 * screen runs its transition BACKWARDS - measured on /solutions, the title fell
 * 95px and faded to nothing at 2099ms before rising again at 2748ms. That is
 * the "animation happens twice" report: one reverse, one forward, neither of
 * them asked for.
 *
 * Here the re-arm happens immediately, while the overlay is still up, and with
 * transitions suppressed so it cannot be seen either way. The replay is then
 * tied to the overlay actually lifting rather than to a guessed delay, so the
 * reader sees one forward entrance and nothing else.
 *
 * @param {() => Array<HTMLElement|null|undefined>} getElements
 * @param {{ timeout?: number }} [options] safety net if no view ever announces
 * @returns {() => void} disposer, for beforeUnmount
 */
export function playOnReveal(getElements, { timeout = 2500 } = {}) {
   const collect = () => (getElements() || []).filter(Boolean);

   // Already on screen, so there is nothing to defer and nothing to hide. A
   // hero that mounts after its view has revealed - TemplateRenderer resolves
   // `<component :is>` from the store, so that is the normal case there - must
   // be left exactly as AOS found it. Hiding it here is the only thing that
   // could ever have made it invisible.
   if (pageRevealed) return () => {};

   let played = false;

   // Puts an element back in its pre-entrance state without that being seen.
   // Removing `aos-animate` from something on screen would otherwise run its
   // transition in reverse, which is the fall-away half of the double
   // animation.
   const rearm = (el) => {
      if (!el.classList.contains('aos-animate')) return;
      const previous = el.style.transition;
      el.style.transition = 'none';
      el.classList.remove('aos-animate');
      // Read back, or the browser coalesces the removal with the restore below
      // and the element never actually sits in the un-animated state.
      void el.offsetWidth;
      el.style.transition = previous;
   };

   collect().forEach(rearm);

   // AOS can run its scan at any point before the overlay lifts - custom.js
   // initialises it on window load, components on mount - and it adds
   // `aos-animate` as soon as the element is in view, which behind an overlay
   // means the entrance is spent before anyone can see it. Anything added
   // before the reveal is quietly undone. Removing the class trips this
   // observer once more, finds nothing to do, and stops.
   let observer = null;
   if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(() => {
         if (played) return;
         collect().forEach(rearm);
      });
      collect().forEach((el) => observer.observe(el, {
         attributes: true, attributeFilter: ['class'],
      }));
   }

   let timer = null;

   const stop = () => {
      played = true;
      document.removeEventListener(PAGE_REVEALED, play);
      if (timer) { clearTimeout(timer); timer = null; }
      if (observer) { observer.disconnect(); observer = null; }
   };

   function play() {
      if (played) return;
      stop();
      // Next frame, so the restored transition is in force before the class
      // that triggers it lands.
      window.requestAnimationFrame(() => {
         collect().forEach((el) => el.classList.add('aos-animate'));
         AOS.refresh();
      });
   }

   timer = setTimeout(play, timeout);
   document.addEventListener(PAGE_REVEALED, play);

   return stop;
}
